import { Component, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from '../../shared/core/state';
import { CourseItem } from '../../shared/core/state/course/models';
import { Pagination } from '../../shared/models/pagination.model';
import * as CourseActions from '../../shared/core/state/course/course.actions';
import { Router } from '@angular/router';
import { OrderService } from '../../shared/core/state/order/order.service';
import { PendingCourseOrder } from '../../shared/core/state/order/order.model';
import {
  SUBJECT_OPTIONS_BY_AUDIENCE,
  TARGET_AUDIENCE_OPTIONS,
} from '../../shared/util/sri-lanka-data';

@Component({
  selector: 'black-course-index',
  templateUrl: './course-index.component.html',
  styleUrls: ['./course-index.component.css'],
  standalone: false,
})
export class CourseIndexComponent implements OnInit {
  private store = inject<Store<State>>(Store);
  private router = inject(Router);
  private orderService = inject(OrderService);

  courseItems: CourseItem[] = [];
  filteredCourses: CourseItem[] = [];
  pendingOrderStatusMap = new Map<string, PendingCourseOrder>();

  targetAudienceOptions = TARGET_AUDIENCE_OPTIONS;
  selectedAudience = '';
  selectedSubject = '';
  availableSubjects: string[] = [];
  searchQuery = '';

  pagination: Pagination = {
    totalDocs: 0,
    limit: 12,
    totalPages: 0,
    page: 1,
    pagingCounter: 1,
    hasPrevPage: false,
    hasNextPage: false,
    prevPage: 0,
    nextPage: 0,
  };

  ngOnInit(): void {
    this.store.dispatch(CourseActions.fetchCourseItems({ page: 1 }));

    this.orderService
      .getPendingCourseOrders()
      .subscribe((map) => (this.pendingOrderStatusMap = map));

    this.store
      .select((state) => state.courses.courseItems)
      .subscribe((items) => {
        this.courseItems = items || [];
        this.applyFilters();
      });

    this.store
      .select((state) => state.courses.pagination)
      .subscribe((result) => {
        if (result) {
          this.pagination = result;
        }
      });
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.applyFilters();
  }

  onAudienceChange(audience: string): void {
    this.selectedAudience = audience;
    this.availableSubjects = audience
      ? SUBJECT_OPTIONS_BY_AUDIENCE[audience] || []
      : [];
    this.selectedSubject = '';
    this.applyFilters();
  }

  onSubjectChange(subject: string): void {
    this.selectedSubject = subject;
    this.applyFilters();
  }

  clearFilters(): void {
    this.selectedAudience = '';
    this.selectedSubject = '';
    this.availableSubjects = [];
    this.searchQuery = '';
    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.courseItems];

    if (this.selectedAudience) {
      filtered = filtered.filter(
        (c) => c.targetAudience === this.selectedAudience
      );
    }

    if (this.selectedSubject) {
      filtered = filtered.filter(
        (c) => c.subject === this.selectedSubject
      );
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (c) =>
          c.title?.toLowerCase().includes(q) ||
          c.subject?.toLowerCase().includes(q) ||
          c.targetAudience?.toLowerCase().includes(q) ||
          c.details?.descriptionSummary?.toLowerCase().includes(q) ||
          c.details?.description?.toLowerCase().includes(q)
      );
    }

    this.filteredCourses = filtered;
  }

  getCourseCardStatus(item: CourseItem): {
    isEnrolled: boolean;
    enrollmentStatus?: 'ACTIVE' | 'AWAITING_APPROVAL' | 'PENDING' | 'OVERDUE';
    orderId?: string;
  } {
    const pending = this.pendingOrderStatusMap.get(item._id);
    if (item.enrollments && item.enrollments.length > 0) {
      return {
        isEnrolled: pending?.status !== 'OVERDUE',
        enrollmentStatus: pending ? pending.status : 'ACTIVE',
        orderId: pending?.orderId,
      };
    }
    if (pending) {
      return {
        isEnrolled: false,
        enrollmentStatus: pending.status,
        orderId: pending.orderId,
      };
    }
    return { isEnrolled: false };
  }

  paginateCourseItems(page: number): void {
    if (page < 1 || page > this.pagination.totalPages) return;
    this.store.dispatch(CourseActions.fetchCourseItems({ page }));
  }

  createRange(totalPages: number): number[] {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  onClickCourse(id: string): void {
    this.router.navigate(['/courses', id]);
  }
}
